import "@tento-ui/components/line-chart/line-chart-body";

import { PlotRequest } from "@tento-data";
import type {
  CanvasRenderTarget,
  CanvasSeriesRenderer,
} from "@tento-data-ui";
import { toCanvasX, toCanvasY } from "@tento-data-ui";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { BarChartIcon, LonaIcons } from "@tento-ui/ui/icons";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { ChartViewModel } from "@tento-lona/platform";
import { LineChartBody } from "@tento-ui/components/line-chart/line-chart-body";
import { LiveData } from "@tento-core/livedata";
import { NumRangeLike } from "@tento-core/range";
import { NaiveDate } from "@tento-chrono";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { Tracing } from "@tento-core/tracing";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { DataStore } from "../alias/data/data-store.ts";
import { DataLegend } from "../alias/data/legend.ts";
import type { PlotFrame } from "../alias/data/plot-frame.ts";
import { type SheetRow } from "@tento-lona";
import { DATA_FACADE, type Option } from "@tento-lona/sheets";
import { CANVAS_LAYOUT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { DefaultBinderHelpers } from "@tento-lona/sheets-ui/sheet/binder/default-binder-helpers.ts";
import type { CanvasRendererProvider } from "@tento-lona/sheets-ui/sheet/canvas-renderer-provider.ts";
import type { Size } from "@tento-ui/layout-utils";

type CanvasCapabilityPlugin = {
  contentProviders?: Option<{
    canvas?: CanvasRendererProvider;
  }>;
};

/**
 * Walk the canvas layout row's children; for each child, find a
 * capability plugin with `contentProviders.canvas` and ask for the
 * row's renderer. First non-null per child wins. Mirrors the
 * timeline-row + calendar-month discovery patterns.
 */
function collectCanvasRenderers(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): CanvasSeriesRenderer[] {
  const renderers: CanvasSeriesRenderer[] = [];
  for (const child of row.children() ?? []) {
    for (const plugin of DefaultBinderHelpers.resolveRowCapabilityPlugins(child, pluginManager)) {
      const provider = (plugin as CanvasCapabilityPlugin).contentProviders
        ?.canvas;
      if (!provider) continue;
      const renderer = provider.rendererForRow(child);
      if (!renderer) continue;
      renderers.push(renderer);
      break; // first match per child
    }
  }
  return renderers;
}

function resolveCanvasDataRow(
  parentRow: SheetRow.Joined.Handle,
): SheetRow.Joined.Handle {
  const lookupKey = parentRow.lookupKey;
  if (lookupKey != null) {
    const matchingSource = findDataFacadeRowByLookupKey(parentRow, lookupKey);
    if (matchingSource != null) return matchingSource;
  }
  return parentRow.findRowWithFacade(DATA_FACADE) ?? parentRow;
}

function findDataFacadeRowByLookupKey(
  row: SheetRow.Joined.Handle,
  lookupKey: string,
): Option<SheetRow.Joined.Handle> {
  if (row.lookupKey === lookupKey && row.as(DATA_FACADE) != null) return row;
  for (const child of row.children()) {
    const found = findDataFacadeRowByLookupKey(child, lookupKey);
    if (found != null) return found;
  }
  return null;
}

/**
 * True when the visible range has scrolled within one full visible-span of
 * either buffer edge — i.e. the canvas buffer no longer has comfortable margin
 * and should be recentered + reloaded. While false, the canvas already covers
 * the viewport (+ margin) and rides native scroll untouched.
 *
 * The trigger fires a FULL span before the edge (was ½ span). With the ±2-span
 * buffer below, that leaves ~1 span of painted runway beyond the visible edge —
 * enough for the async reload (`getAllInRangeSpanning` awaits range loading) to
 * land before the visible range reaches unpainted territory. Too little runway
 * is the "catch-up" snap: the canvas stalls at the old anchor, then jumps when
 * the reload finally repositions/repaints it.
 */
function nearBufferEdge(
  visStart: NaiveDate,
  visEnd: NaiveDate,
  buffer: { start: NaiveDate; end: NaiveDate },
): boolean {
  const spanDays = Math.max(1, visEnd.dse - visStart.dse);
  const slack = spanDays;
  return visStart.dse - buffer.start.dse < slack ||
    buffer.end.dse - visEnd.dse < slack;
}

/**
 * The layout plugin that draws a data row's series on one canvas across the
 * columns.
 */
export class CanvasLayoutPlugin implements RenderPlugin.Layout<
  DataCanvasCell,
  DataLegend
> {
  static readonly spec: PerTypeSpec = CANVAS_LAYOUT_SPEC;

  readonly role = "layout";
  readonly id = "canvas";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(BarChartIcon);

  #stateManager = new AsyncStateManager();
  /**
   * One `DataCanvasCell` per canvas row id, painted ONCE over a BUFFER range
   * (visible ± a margin) and revealed as a single fixed child. A fixed child is
   * positioned at a content-x inside the scrolled `#content`, so it rides native
   * scroll for free (compositor) between recenters — no per-scroll reload, no
   * drift. We reload + re-reveal ONLY when the visible range nears a buffer edge
   * (see `onViewportColumnsChanged`); otherwise the canvas keeps riding scroll.
   * One surface → no seams. Per-column `bindCell` is a height-holder.
   */
  #spanCells = new Map<string, DataCanvasCell>();
  /** Loaded buffer date range per canvas row id; drives the recenter gate. */
  #buffers = new Map<string, { start: NaiveDate; end: NaiveDate }>();
  /**
   * Per-key monotonic reveal epoch. Only the latest recenter's async reveal
   * wins: bumped synchronously at each recenter, checked in the load callback.
   * At week/zoom scale `nearBufferEdge` trips constantly, so several async
   * recenters can resolve within one frame; without this, each makes a FRESH
   * `DataCanvasCell` (the `isConnected` reuse gate is false during the deferred
   * `$$.mutate` append), the per-element `AsyncStateManager` guard never
   * cancels across the distinct cells, and the id-dedup in `revealDates` misses
   * the still-pending append — so two spans append under the same
   * `canvas-span:${key}` id and paint two offset curves. Keying the guard on the
   * row (not the element) makes exactly one reveal win regardless of the cell
   * churn or load resolve-order.
   */
  #revealEpoch = new Map<string, number>();
  /**
   * The last viewport dispatch per canvas row, cached so `repaintData` can
   * replay it. Valid exactly as long as the viewport is unchanged — which is
   * precisely the situation repaintData exists for (same range, new data).
   */
  #lastViewport = new Map<
    string,
    {
      row: SheetRow.Joined.Handle;
      columns: ColumnIndex.Key.Resolved.Default.Range;
      handle: SheetDelegate;
    }
  >();

  readonly #store: DataStore;
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    store: DataStore,
  ) {
    this.#store = store;
  }

  /**
   * Reload every painted chart from the store and repaint in place — the
   * LIVE-data path. A cell write already dirties the row's data facade, but
   * nothing repaints: the riding-scroll buffer deliberately suppresses
   * reloads while the visible range is unchanged, and the sheet's viewport
   * dispatch dedupes on the visible-columns key, so "same range, new data"
   * has no route to the canvas without this. Dropping the buffer and
   * replaying the last dispatch runs the normal recenter — the store re-reads
   * the dirty facade, `toXRange` is re-evaluated (a live preset's trailing
   * window slides to now), and the async epoch/`stillValid` guards keep
   * overlapping repaints single-winner exactly as for scroll recenters.
   */
  repaintData(): void {
    for (const [key, ctx] of this.#lastViewport) {
      this.#buffers.delete(key);
      this.onViewportColumnsChanged(ctx.row, ctx.columns, ctx.handle, null);
    }
  }

  makeCell(): DataCanvasCell {
    return DataCanvasCell.make();
  }

  // The chart is painted ONCE by the single buffered canvas
  // (`onViewportColumnsChanged` → `fixedChild.revealDates`), so the per-column
  // cell paints nothing — it is just a height-holder. This skips N per-column
  // data loads (one buffered load instead) and removes the per-column slope
  // shear / view-window desync. The cell keeps its layout-assigned height.
  async bindCell($cell: DataCanvasCell, _context: BindCellContext) {
    $cell.renderEmpty();
  }

  /**
   * Paint the row's chart as a SINGLE canvas over a BUFFER range (visible ± a
   * margin), revealed as one fixed child. Because the fixed child sits at a
   * content-x inside the scrolled `#content`, it rides native scroll (the
   * compositor carries it with the columns) between recenters — no per-scroll
   * reload, no drift. This fires on visible column-set change (not per frame);
   * we reload + re-reveal ONLY when the visible range nears a buffer edge, and
   * otherwise return early so the canvas keeps riding scroll.
   */
  onViewportColumnsChanged(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    _$legend: Option<DataLegend>,
  ): void {
    const parentRow = row.parent();
    if (!parentRow) return;

    const key = row.id.toString();
    // Cache the dispatch before any early return — `repaintData` replays it
    // to force a reload under an unchanged viewport.
    this.#lastViewport.set(key, { row, columns, handle });
    const visStart = columns.start.pd.toRange().start;
    const visEnd = columns.end.pd.toRange().end;

    // Ride native scroll: while the buffer still has comfortable margin around
    // the visible range, do nothing — the canvas is already painted and the
    // compositor moves it with the columns. Only recenter at the buffer edge
    // (or on first bind, when there is no buffer yet).
    const buffer = this.#buffers.get(key);
    const $existingSpan = this.#spanCells.get(key);
    // "Riding scroll" only holds while the painted canvas is STILL attached (the
    // compositor is carrying it). `spanCells`/`buffers` persist on this singleton
    // plugin across sheet teardowns, so after a reopen the cached buffer is stale
    // and the cached `$span` was detached — without the connected check we'd
    // early-return and never re-reveal the body, leaving the chart blank.
    const ridingScroll =
      buffer != null &&
      $existingSpan != null &&
      $existingSpan.isConnected &&
      !nearBufferEdge(visStart, visEnd, buffer);
    if (ridingScroll) {
      // Riding native scroll — the canvas is already painted and the compositor
      // moves it with the columns; no reload/re-reveal for this viewport change.
      return;
    }

    // Recenter: buffer = visible ± TWO visible-spans margin (≈5× viewport),
    // measured in days so it scales across timescales. Widened from ±1 span (3×):
    // it halves recenter frequency on narrow day views and, together with the
    // 1-span `nearBufferEdge` slack, gives the async reload a full span of runway
    // so the canvas reload lands before the painted edge is reached (no snap).
    const spanDays = Math.max(1, visEnd.dse - visStart.dse);
    const bufStart = visStart.sub({ days: 2 * spanDays });
    const bufEnd = visEnd.add({ days: 2 * spanDays });
    this.#buffers.set(key, { start: bufStart, end: bufEnd });

    // Commit to this recenter: bump the row's reveal epoch so any earlier
    // in-flight load for this key is now stale and its `.then` will bail (below),
    // leaving exactly one span to reveal.
    const epoch = (this.#revealEpoch.get(key) ?? 0) + 1;
    this.#revealEpoch.set(key, epoch);

    // Pixel positioning. The canvas must span the whole buffer (≈5× viewport),
    // whose left/right columns are OFF-SCREEN. We anchor via `revealDates` at the
    // FIRST VISIBLE date (a laid-out column → resolves + gets per-row Y, including
    // the `rebindFixedChildYPositions` re-resolve that `revealLive` would skip),
    // give it an EXPLICIT pixel width (not "fill", which needs both endpoints laid
    // out), and shift it left by two viewports via a negative `inset.left` so it
    // spans the full buffer. Anchored to a content column → rides native scroll.
    const visStartIdx = columns.start.offset;
    const visEndIdx = columns.end.offset;
    // INCLUSIVE column count (canonical `visibleColumnCount` = end - start + 1,
    // sheet.ts). This MUST match the column count implied by `spanDays`, which is
    // measured across the FULL inclusive range (`start.toRange().start` ..
    // `end.toRange().end`). Using `end - start` (one short) made the pixel width
    // (`bufferWidthPx`/`leftMarginPx`) span one fewer column than the date range
    // it paints, compressing the chart to `(n-1)/n` of true column pitch and
    // snapping the accumulated error at every recenter (the "catch-up" jump).
    const visibleCols = Math.max(1, visEndIdx - visStartIdx + 1);
    const cellWidthPx = handle.state.getColumnWidthPx(visStartIdx);
    const leftMarginPx = 2 * visibleCols * cellWidthPx;
    const bufferWidthPx = 5 * visibleCols * cellWidthPx;
    Tracing.log("data-canvas/recenter", {
      row: key,
      buf: bufStart.toString() + ".." + bufEnd.toString(),
      vis: visStart.toString() + ".." + visEnd.toString(),
      visibleCols,
      cellWidthPx: Math.round(cellWidthPx),
      leftMarginPx: Math.round(leftMarginPx),
      bufferWidthPx: Math.round(bufferWidthPx),
    });

    const sheet = handle.state.sheet;
    const dataRow = resolveCanvasDataRow(parentRow);
    const renderers = collectCanvasRenderers(row, handle.pluginManager);
    const tz = columns.start.tz;
    const bufferRange = new NaiveDate.Range(bufStart, bufEnd);

    // Reuse the cached canvas only while it's still attached (a genuine
    // riding-scroll recenter). After a reopen the cached cell was detached (and
    // may have been pool-recycled), so make a fresh one to re-reveal.
    const cachedSpan = this.#spanCells.get(key);
    const $span =
      cachedSpan != null && cachedSpan.isConnected
        ? cachedSpan
        : DataCanvasCell.make();
    this.#spanCells.set(key, $span);
    // The canvas is a fixed child, so it is NOT sized by the row binder — give
    // it the row's explicit cell height + the computed buffer width. Without the
    // height the cell's `height: 100%` stretches to the whole sheet.
    $span.style.height = row.layoutSpec(sheet.timescale).cellHeight + "px";
    $span.style.width = bufferWidthPx + "px";

    const dataPromise = this.#store.getAllInRangeSpanning(
      sheet,
      dataRow,
      bufferRange,
      tz,
      renderers,
    );
    const stillValid = this.#stateManager.bind($span, dataPromise);
    dataPromise
      .then(({ xRange, vm, renderer, plotFrame, plotRenderer }) => {
        if (!stillValid(dataPromise)) return;
        // Superseded by a newer recenter for this row — don't reveal a second
        // span. Element-keyed `stillValid` can't catch this because each
        // recenter minted a fresh cell; the row-keyed epoch does.
        if (this.#revealEpoch.get(key) !== epoch) return;
        const extent = plotFrame?.yExtent ?? null;
        const scale = extent
          ? this.#store.resolveChartScale(parentRow.id, vm, extent)
          : null;
        $span.bind({
          vm,
          xRange,
          renderer,
          plotFrame,
          plotRenderer,
          yScaler: scale?.yScaler ?? null,
        });
        // Anchor at the first visible date (laid-out → resolves + gets per-row Y
        // via revealDates' rebind), explicit pixel width, shifted left one
        // viewport so it spans the buffer. Rides native scroll between recenters.
        handle.fixedChild.revealDates(
          `canvas-span:${key}`,
          $span,
          { x: visStart, y: row.id },
          {
            widthBehavior: bufferWidthPx,
            inset: { left: -leftMarginPx, top: 0 },
            animate: false,
          },
        );
        if (scale?.changed) void handle.rebind.rebindRowLegend(row);
      })
      .catch(() => {});
  }

  // The data rows are subsumed under this canvas, so the canvas is the one
  // visible body row for the chart — it carries the rich data legend (label +
  // show/hide toggle). DataLegend.factory resolves the DataAliasPlugin + label
  // from the parent data alias row.
  legendFactory: SheetLegendClass<DataLegend> = DataLegend.factory;

  loadData = null;

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onBindRow(_handle: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onResize($cell: DataCanvasCell, context: BindCellContext): void {
    // On a width change the sheet invokes onResize (not bindCell). The cell
    // carries an EXPLICIT `style.width` (set in bindCell via setCurrentSize), so
    // without updating it here the cell keeps its old width on resize — its
    // ResizeObserver never sees a contentRect change, the canvas never
    // re-renders, and the chart cells don't stretch to the new column width
    // (leaving mismatched-width canvases / bunched candles). Re-apply the new
    // width so the observer fires and re-renders at the correct size.
    $cell.setCurrentSize(context.widthPx);
  }
  onRenderedColumnsWillChange(): void {}
}

/**
 * Vertical inset (top + bottom) of the plot area, in CSS px. Matches the
 * `yPadding` the legend (`std-line-chart-legend`) uses so candle extremes
 * line up with the axis ticks and don't touch the canvas edges.
 */
const Y_PADDING_PX = 20;


/**
 * "Nice" round tick values across `[min, max]` (~`count` of them), for y-axis
 * gridlines. Steps snap to 1/2/5 × 10ⁿ so lines land on readable values.
 */
function niceTicks(min: number, max: number, count: number): number[] {
  const span = max - min;
  if (!(span > 0)) return [min];
  const rawStep = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const ticks: number[] = [];
  for (let t = Math.ceil(min / step) * step; t <= max + step * 1e-6; t += step) {
    ticks.push(t);
  }
  return ticks;
}

/** Resolve a theme color token to a value the canvas accepts.
 *
 * `getPropertyValue("--divider")` returns the DECLARED value, and for the
 * theme tokens that is the raw `light-dark(#dadce0, #444)` literal — which
 * `ctx.strokeStyle` silently REJECTS, leaving the context on whatever the
 * previous renderer set. Measured: every gridline stroked in its chart's
 * series colour. Computed `color` DOES resolve `light-dark()` against the
 * element's color-scheme, so route the token through it: set `color` to the
 * var (with the fallback inside the var, so a missing token also resolves),
 * read it back computed, restore. One style-recalc per paint — the paint runs
 * at repaint cadence (1 Hz live), not per frame. */
function resolveCanvasColor(
  $el: HTMLElement,
  cssVar: string,
  fallback: string,
): string {
  const prev = $el.style.color;
  $el.style.color = `var(${cssVar}, ${fallback})`;
  const resolved = getComputedStyle($el).color;
  $el.style.color = prev;
  return resolved.length > 0 ? resolved : fallback;
}

/** The canvas a data row's chart is drawn on. */
@component({ name: "std-data-canvas-cell" })
export class DataCanvasCell extends LonaWebComponent {
  #$body: LineChartBody = this.hydrate("body");
  #$plot: HTMLCanvasElement = this.$("plot");
  #vm: Option<ChartViewModel> = null;
  #size: Option<Size> = null;
  #_lastBindProps: Option<LineChartBody.Props> = null;
  #plotFrame: Option<PlotFrame> = null;
  #plotRenderer: readonly CanvasSeriesRenderer[] = [];
  // Shared y-scaler (also bound by the legend). The plot is painted against
  // its live range so a legend drag rescales the candles; `null` falls back
  // to the frame's auto-fit extent.
  #yScaler: Option<ChartViewModel.YScaler> = null;

  #ro = new ResizeObserver(([entry]) => {
    if (entry === undefined) return;
    const domSize = {
      width: entry.contentRect.width || this.offsetWidth,
      height: entry.contentRect.height || this.offsetHeight,
    };
    // Cell going off-screen: skip height-0 to avoid re-rendering with a 0-height canvas
    if (domSize.height === 0) return;
    this.#size = domSize;
    // Update the VM's stored size without firing vm.invalidates — that would cascade
    // to ALL cells sharing this VM (same row, different columns) and cause cells whose
    // sparse array was cleared by resetIfDisjoint to re-render with empty data.
    this.#vm?.bindSize(domSize, false);
    this.#$body.rebindSize(domSize);
    // Re-render only THIS cell with the updated size.
    if (this.#plotFrame != null) {
      this.#renderPlotFrame();
    } else if (this.#_lastBindProps) {
      this.#$body.bind(this.#_lastBindProps);
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#ro.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ro.unobserve(this);
    this.unbind();
  }

  unbind(): void {
    this.#vm?.invalidates.removeListener(this);
    this.#vm = null;
    this.#_lastBindProps = null;
    this.#plotFrame = null;
    this.#plotRenderer = [];
  }

  /**
   * Render nothing — the per-column cell is a height-holder when the row's
   * chart is painted by the single spanning overlay (B0). Drops the vm
   * listener so a shared-vm invalidation does not repaint this hidden cell.
   */
  renderEmpty(): void {
    this.unbind();
    this.#$body.style.display = "none";
    this.#$plot.style.display = "none";
  }

  setCurrentSize(widthPx: number): void {
    this.style.width = widthPx + "px";
    const height = this.#size?.height || this.offsetHeight;
    if (height > 0) {
      this.#size = { width: widthPx, height };
    }
    // If height is still 0 (cell not yet visible), leave this.size as-is.
    // ResizeObserver will update it once the cell enters the viewport.
  }

  bind(props: {
    vm: ChartViewModel;
    xRange: NumRangeLike;
    renderer: LineChartBody.Renderer[];
    plotFrame: Option<PlotFrame>;
    plotRenderer: readonly CanvasSeriesRenderer[];
    yScaler: Option<ChartViewModel.YScaler>;
  }): void {
    this.#vm?.invalidates.removeListener(this);
    this.#vm = props.vm;
    this.#yScaler = props.yScaler;
    if (this.#size && this.#size.height > 0) this.#vm.bindSize(this.#size, false);

    Tracing.log("data-canvas/cell-bind", {
      hasSize: this.#size != null,
      sizeWidth: this.#size?.width ?? null,
      sizeHeight: this.#size?.height ?? null,
      offsetWidth: this.offsetWidth,
      offsetHeight: this.offsetHeight,
      plotFramePresent: props.plotFrame != null,
      plotRendererCount: props.plotRenderer.length,
      legacyRendererCount: props.renderer.length,
      plotKeys: props.plotFrame?.collection.keys() ?? [],
    });

    if (props.plotFrame != null && props.plotRenderer.length > 0) {
      this.#plotFrame = props.plotFrame;
      this.#plotRenderer = props.plotRenderer;
      this.#_lastBindProps = null;
      this.#$body.style.display = "none";
      this.#$plot.style.display = "block";
      props.vm.invalidates.addListener(
        this,
        () => this.#renderPlotFrame(),
        LiveData.TriggerImmediately,
      );
      return;
    }

    this.#plotFrame = null;
    this.#plotRenderer = [];
    this.#$body.style.display = "block";
    this.#$plot.style.display = "none";

    const { vm, xRange, renderer } = props;
    const bodyProps: LineChartBody.Props = { vm, xRange, renderer, yPadding: 20 };
    this.#_lastBindProps = bodyProps;
    vm.invalidates.addListener(
      this,
      () => this.#$body.bind(bodyProps),
      LiveData.TriggerImmediately,
    );
  }

  #renderPlotFrame(): void {
    if (this.#plotFrame == null) return;
    const size = this.#size ?? {
      width: this.offsetWidth,
      height: this.offsetHeight,
    };
    if (size.width <= 0 || size.height <= 0) {
      Tracing.log("data-canvas/render-skip", {
        reason: "empty-size",
        width: size.width,
        height: size.height,
        offsetWidth: this.offsetWidth,
        offsetHeight: this.offsetHeight,
      });
      return;
    }

    const ctx = this.#$plot.getContext("2d");
    if (ctx == null) {
      Tracing.log("data-canvas/render-skip", {
        reason: "no-context",
        width: size.width,
        height: size.height,
      });
      return;
    }

    const pixelRatio = window.devicePixelRatio || 1;
    const pixelWidth = Math.max(1, Math.round(size.width * pixelRatio));
    const pixelHeight = Math.max(1, Math.round(size.height * pixelRatio));
    if (this.#$plot.width !== pixelWidth) this.#$plot.width = pixelWidth;
    if (this.#$plot.height !== pixelHeight) this.#$plot.height = pixelHeight;
    this.#$plot.style.width = size.width + "px";
    this.#$plot.style.height = size.height + "px";

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    // Inset the plot vertically by Y_PADDING_PX so the candles map to the
    // same band the legend ticks do (`LinearYScaler.d2c` insets by the same
    // padding), and so the extremes don't touch the canvas edges.
    const pad = Y_PADDING_PX;
    const plotHeight = Math.max(1, size.height - 2 * pad);
    ctx.translate(0, pad);

    const target: CanvasRenderTarget = {
      ctx,
      size: { width: size.width, height: plotHeight },
    };
    // Paint against the shared y-scaler's LIVE range (so a legend drag
    // rescales the candles); fall back to the frame's auto-fit extent.
    const liveRange = this.#yScaler?.yConfig.range;
    const yExtent = liveRange
      ? { min: liveRange.min, max: liveRange.max }
      : this.#plotFrame.yExtent;
    const request = PlotRequest.make(
      this.#plotFrame.xRange,
      this.#plotFrame.paddedRange,
      yExtent,
    );

    // Horizontal y-gridlines at nice tick values over the extent, painted
    // behind the series so the chart has visible reference lines + breathing
    // room (the legend labels the same axis on the left).
    if (yExtent != null && yExtent.min < yExtent.max) {
      // Gridlines at the SAME tick values the legend labels (the shared
      // yScaler's `getTicks()`), so the lines line up with the y-axis labels;
      // fall back to round ticks when no scaler is bound (auto-fit, no legend).
      const gridTicks = this.#yScaler?.getTicks() ??
        niceTicks(yExtent.min, yExtent.max, 4);
      const grid = resolveCanvasColor(this, "--divider", "rgba(128, 128, 128, 0.15)");
      ctx.save();
      ctx.strokeStyle = grid;
      ctx.globalAlpha = 0.4; // subtle reference lines, not heavy rules
      ctx.lineWidth = 1;
      for (const tick of gridTicks) {
        if (tick < yExtent.min || tick > yExtent.max) continue;
        // EXACT (unrounded) position: the legend tick's `d2c` and the series
        // points are both unrounded, and `legend fromTop === pad + toCanvasY`
        // exactly — so `Math.round(...) + 0.5` (a crispness hack) would shift the
        // gridline up to ~0.5px off BOTH the ticks and the data. A hair softer,
        // but it lands on the same sub-pixel line as everything else.
        const cy = toCanvasY(tick, yExtent, plotHeight);
        ctx.beginPath();
        ctx.moveTo(0, cy);
        ctx.lineTo(size.width, cy);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Vertical x-gridlines at the positions the preset's `xTicks` closure
    // chose (already evaluated into the frame). Same stroke as the y-grid so
    // both axes read as one reference grid. GRIDLINES ONLY — the time labels
    // belong to the sheet's footer timeline-legend row, never to a data row.
    const xTicks = this.#plotFrame.xTicks;
    if (xTicks != null && xTicks.length > 0) {
      const gridColor = resolveCanvasColor(this, "--divider", "rgba(128, 128, 128, 0.15)");
      const xRange = this.#plotFrame.xRange;

      ctx.save();
      ctx.strokeStyle = gridColor;
      ctx.globalAlpha = 0.4; // subtle reference lines, not heavy rules
      ctx.lineWidth = 1;
      for (const tick of xTicks) {
        // Unrounded, exactly like the y-grid and the series renderers — a
        // `+ 0.5` crispness hack would shift the line off the data it marks.
        const cx = toCanvasX(tick, xRange, size.width);
        ctx.beginPath();
        ctx.moveTo(cx, 0);
        ctx.lineTo(cx, plotHeight);
        ctx.stroke();
      }
      ctx.restore();
    }

    // The now-cursor: one vertical line at the position the preset's `xNow`
    // closure returned (already evaluated into the frame), drawn in the same
    // pass as the x-gridlines because it is the same CLASS of ink — reference,
    // not chrome. It marks the pen tip the live curve is being drawn at, so it
    // is the accent (`--blue`, the token the chart code already reads for a
    // series colour) rather than `--divider`, and a shade more present than the
    // grid's 0.4 so it reads as "this instant" among the reference lines.
    // Still GRIDLINE-CLASS ink: no label rides on it — the times stay in the
    // footer timeline-legend row, like every other x-axis label.
    const xNow = this.#plotFrame.xNow;
    if (xNow != null) {
      const accent = resolveCanvasColor(this, "--blue", "#2d5f8d");
      ctx.save();
      ctx.strokeStyle = accent;
      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 1;
      // Unrounded, for the same reason the ticks are: the cursor has to land
      // on the same sub-pixel line as the newest sample it is chasing.
      const cx = toCanvasX(xNow, this.#plotFrame.xRange, size.width);
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, plotHeight);
      ctx.stroke();
      ctx.restore();
    }

    const entries = this.#plotFrame.collection.entries();
    let supportedCount = 0;
    const renderStart = performance.now();
    for (const renderer of this.#plotRenderer) {
      for (const entry of entries) {
        if (!renderer.supports(entry)) continue;
        supportedCount += 1;
        renderer.render(target, request, entry);
      }
    }
    const renderMs = Math.round((performance.now() - renderStart) * 100) / 100;
    Tracing.log("data-canvas/render", {
      width: size.width,
      height: size.height,
      pixelWidth,
      pixelHeight,
      renderMs,
      rendererCount: this.#plotRenderer.length,
      entryCount: entries.length,
      supportedCount,
      keys: this.#plotFrame.collection.keys(),
      shapes: entries.map((entry) =>
        `${entry.descriptor.key}:${entry.descriptor.shape.kind}`
      ),
      yRanges: entries.map((entry) =>
        `${entry.descriptor.key}:${JSON.stringify(entry.descriptor.yRange)}`
      ),
      pointCounts: entries.map((entry) =>
        `${entry.descriptor.key}:${entry.series.points().length}`
      ),
      smoothedCounts: entries.map((entry) =>
        `${entry.descriptor.key}:${entry.derived?.smoothed?.points().length ?? 0}`
      ),
      segmentCounts: entries.map((entry) =>
        `${entry.descriptor.key}:${entry.derived?.segments?.length ?? 0}`
      ),
      segmentSizes: entries.map((entry) =>
        `${entry.descriptor.key}:[${(entry.derived?.segments ?? []).map((s) => s.points().length).join(",")}]`
      ),
      renderers: this.#plotRenderer.map((renderer) => renderer.constructor.name),
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        height: 100%;
        width: 100%;
      }

      #body {
        height: 100%;
        width: 100%;
      }

      #plot {
        display: none;
        height: 100%;
        width: 100%;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <canvas id="plot"></canvas>
    <std-line-chart-body id="body"></std-line-chart-body>
  `;
}

/** Types that go with `CanvasLayoutPlugin`: `Attributes`. */
export namespace CanvasLayoutPlugin {
  export type Attributes = Record<string, never>;
}
